import { FunctionComponent, ReactNode } from "react";

import AppLogo from "@/assets/images/favicon.png";

export interface ChatChip {
	label: string;
	onClick: () => void;
	/**
	 * Survives `clearChips`. A question's options are gone once it is answered,
	 * but a standing affordance — "let me adjust my targeting" — has to outlive
	 * the beats that follow it (PREC-3904).
	 */
	sticky?: boolean;
}

const AiAvatar: FunctionComponent = () => (
	<img
		alt="Signalist"
		className="h-7 w-7 flex-shrink-0 rounded-full border border-gray-200 bg-white object-contain p-0.5"
		src={AppLogo}
	/>
);

export const AiMessage: FunctionComponent<{ children: ReactNode; chips?: ChatChip[] }> = ({
	children,
	chips,
}) => (
	<div className="mb-4 flex items-start gap-2.5">
		<AiAvatar />
		<div className="min-w-0">
			<div className="max-w-[420px] rounded-2xl rounded-tl-sm bg-gray-100 px-4 py-3 text-ssm leading-relaxed">
				{children}
			</div>
			{chips && chips.length > 0 && (
				<div className="mt-2 flex flex-wrap gap-2">
					{chips.map((chip) => (
						<button
							key={chip.label}
							className="cursor-pointer rounded-full border border-brand-light px-3.5 py-1.5 text-xs font-semibold text-brand-light transition-colors duration-200 hover:bg-blue-action hover:text-white"
							type="button"
							onClick={chip.onClick}
						>
							{chip.label}
						</button>
					))}
				</div>
			)}
		</div>
	</div>
);

export const UserMessage: FunctionComponent<{ children: ReactNode }> = ({ children }) => (
	<div className="mb-4 flex justify-end">
		<div className="max-w-[420px] rounded-2xl rounded-tr-sm bg-blue-action px-4 py-3 text-ssm leading-relaxed text-white">
			{children}
		</div>
	</div>
);

export const TypingIndicator: FunctionComponent = () => (
	<div className="mb-4 flex items-start gap-2.5">
		<AiAvatar />
		<div className="flex items-center gap-1 rounded-2xl rounded-tl-sm bg-gray-100 px-4 py-3.5">
			<span className="h-1.5 w-1.5 animate-bounce rounded-full bg-text-meta [animation-delay:0ms]" />
			<span className="h-1.5 w-1.5 animate-bounce rounded-full bg-text-meta [animation-delay:150ms]" />
			<span className="h-1.5 w-1.5 animate-bounce rounded-full bg-text-meta [animation-delay:300ms]" />
		</div>
	</div>
);

export const IcpHighlight: FunctionComponent<{ children: ReactNode }> = ({ children }) => (
	<span className="px-0 font-semibold text-text-meta">{children}</span>
);

/**
 * The AI-drafted items of a question, one per bullet — a run of 3-4 of them reads
 * as a wall of highlighted text when kept inline in the sentence.
 */
export const IcpHighlightList: FunctionComponent<{ items: string[] }> = ({ items }) => (
	<ul className="my-2 flex flex-col items-start gap-1.5">
		{items.map((item) => (
			<li key={item} className="flex items-start gap-2">
				<span className="mt-3 h-1 w-1 flex-shrink-0 rounded-full bg-text-meta" />
				<IcpHighlight>{item}</IcpHighlight>
			</li>
		))}
	</ul>
);
